import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

// Agents always use the light theme (no dark mode for the agent console).
// ThemeProvider wraps AuthProvider, so we read the profile type from
// localStorage (set by AuthContext.login) rather than via useAuth.
const readIsAgent = () =>
  (typeof localStorage !== 'undefined' && localStorage.getItem('profile_type')) === 'agent';

export const ThemeProvider = ({ children }) => {
  // `agent` is tracked as STATE so login/logout re-renders the provider even
  // when the theme value itself doesn't change (e.g. agent logs in while the
  // theme is already light) — otherwise canToggleTheme / data-theme go stale.
  const [agent, setAgent] = useState(readIsAgent);
  const [theme, setTheme] = useState(() => localStorage.getItem('cvq-theme') || 'light');

  // Re-evaluate the profile on login/logout ('cvq-profile-change') and on
  // other-tab changes ('storage').
  useEffect(() => {
    const onProfileChange = () => setAgent(readIsAgent());
    window.addEventListener('cvq-profile-change', onProfileChange);
    window.addEventListener('storage', onProfileChange);
    return () => {
      window.removeEventListener('cvq-profile-change', onProfileChange);
      window.removeEventListener('storage', onProfileChange);
    };
  }, []);

  // Agents are locked to light; everyone else uses their chosen theme.
  const effectiveTheme = agent ? 'light' : theme;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveTheme);
    // Never persist a theme choice for agents (they're locked to light).
    if (!agent) localStorage.setItem('cvq-theme', theme);
  }, [effectiveTheme, agent, theme]);

  const toggleTheme = () => {
    if (agent) return; // locked to light for agents
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme: effectiveTheme, toggleTheme, canToggleTheme: !agent }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
